"use client";

import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Checkbox,
  useToast,
} from "@chakra-ui/react";
import { useAppTheme } from "../../ThemeContext.tsx";

// Update the ColumnVisibility type to include the new columns
export type ColumnVisibility = {
  time: boolean;
  duration: boolean;
  type: boolean;
  cost: boolean;
  call_id: boolean;
  disconnection_reason: boolean;
  call_status: boolean;
  user_sentiment: boolean;
  from: boolean;
  to: boolean;
  call_successful: boolean;
  latency: boolean;
  recording: boolean;
  logs: boolean;
};

interface CustomizeFieldsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (fields: ColumnVisibility) => void;
  currentVisibility: ColumnVisibility;
}

export const CustomizeFieldsDialog: React.FC<CustomizeFieldsDialogProps> = ({
  isOpen,
  onClose,
  onApply,
  currentVisibility,
}) => {
  const [selectedFields, setSelectedFields] =
    React.useState<ColumnVisibility>(currentVisibility);
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Define theme-based colors
  const modalBg = isDark ? "gray.800" : "white";
  const headerTextColor = isDark ? "white" : "gray.800";

  React.useEffect(() => {
    if (isOpen) {
      setSelectedFields(currentVisibility);
    }
  }, [isOpen, currentVisibility]);

  const handleToggleField = (field: keyof ColumnVisibility) => {
    setSelectedFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleApply = () => {
    try {
      onApply(selectedFields);
      onClose();
    } catch (error) {
      toast({
        title: "Error applying field customization",
        status: "error",
        duration: 3000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader color={headerTextColor}>Customize Fields</ModalHeader>
        <ModalBody color={headerTextColor}>
          <VStack align="start" spacing={3}>
            <Checkbox
              isChecked={selectedFields.time}
              onChange={() => handleToggleField("time")}
            >
              Time
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.duration}
              onChange={() => handleToggleField("duration")}
            >
              Call Duration
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.type}
              onChange={() => handleToggleField("type")}
            >
              Type
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.cost}
              onChange={() => handleToggleField("cost")}
            >
              Cost
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.call_id}
              onChange={() => handleToggleField("call_id")}
            >
              Call ID
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.disconnection_reason}
              onChange={() => handleToggleField("disconnection_reason")}
            >
              Disconnection Reason
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.call_status}
              onChange={() => handleToggleField("call_status")}
            >
              Call Status
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.user_sentiment}
              onChange={() => handleToggleField("user_sentiment")}
            >
              User Sentiment
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.from}
              onChange={() => handleToggleField("from")}
            >
              From
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.to}
              onChange={() => handleToggleField("to")}
            >
              To
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.call_successful}
              onChange={() => handleToggleField("call_successful")}
            >
              Call Successful
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.latency}
              onChange={() => handleToggleField("latency")}
            >
              End to End Latency
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.recording}
              onChange={() => handleToggleField("recording")}
            >
              Recording
            </Checkbox>
            <Checkbox
              isChecked={selectedFields.logs}
              onChange={() => handleToggleField("logs")}
            >
              Logs
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            variant="ghost"
            mr={3}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            colorScheme="gray"
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
